import {
  Button,
  Collapse,
  Icon,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Box,
  Checkbox,
  Divider,
  Radio
} from '@mui/material';
import _ from '@lodash';
import dispatchMessage, { calculateOrderItemTotalAmount, calculateMargin, calculateUnitAmount, calculateUnitPrice, checkNullOrUndefined, formatter, getFileImageSrc, isSupplier, onFileViwerClick, preventNegativeValues, displayName, formatWeight, isValidObject, displaySurcharge } from 'app/main/apps/common/AuraFunctions';
import { calculateSurchargeExact, calculateLineTotalExact, addMoneyExact } from 'app/main/apps/common/money';
import { Fragment, useEffect, useState, memo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { cloneDeep, isEmpty, omit, sumBy } from 'lodash';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import AuraBaseDialog from 'app/main/apps/common/AuraBaseDialog';
import { Loader } from 'app/main/apps/common/ListInfoMessage';
import OrderHeader from 'app/main/apps/common/OrderHeader';
import { AuraSimpleTableHead } from 'app/main/apps/common/AuraSimpleTable';
import { showMessage } from 'app/store/fuse/messageSlice';
import { openDialog } from 'app/store/fuse/dialogSlice';
import showConfirmationDialog from 'app/main/apps/common/AuraSimpleConfirmDialogs';
import { chooseSupplierForArtWork, closeChooseSupplierDialog, getQuotedSuppliers, updateQuotedSuppliersList, saveOrder, updateOrder, openVasDialog, getVasPriceList, getRevisionHistory, openOrderDialog } from '../../store/orderSlice';
import { getOrders } from '../../store/ordersSlice';
import ConfirmAttachments from './ConfirmAttachments';
import { quoteToOrder } from '../../store/rfqsSlice';
import { chooseSupplierSchema } from './OrderBase';
import { getUniqueCategories, hasInValidVASPriceDetails, openVasPreview } from '../Vas/VasHelper';
import VasPriceList from '../Vas/VasPriceList';
import Attachments from '../../shared/Attachments';
import SupplierOrderConfirmationDialog from './SupplierOrderConfirmationDialog';

/** PGSS-4979: true when the cart has both stock and non-stock lines (mixed Create Supplier Order). */
function isMixedSupplierCart(lines) {
  if (!Array.isArray(lines) || lines.length === 0) return false;
  return lines.some((od) => od?.product?.is_stock_product !== true)
    && lines.some((od) => od?.product?.is_stock_product === true);
}


function ChooseSupplier({ isDashboard, loadOrders }) {
  const dispatch = useDispatch();
  const suppliersList = useSelector((state) => state.eCommerceApp.order?.quotedSuppliersList?.orders);
  const parentId = useSelector((state) => state.eCommerceApp?.order?.quotedSuppliersList?.parent_order_details)
  const vasData = useSelector((state) => state.eCommerceApp?.order?.quotedSuppliersList?.vas)
  const [selected, setSelected] = useState('');
  const [accordionIndex, setAccordionIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [next, setNext] = useState(true)
  const [selectedIndex, setSelectIndex] = useState()
  const [approve, setApprove] = useState(false)
  const [isCreateOrder, setIsCreateOrder] = useState(null)
  const [showOrderDrawingConfirmation, setShowOrderDrawingConfirmation] = useState(null)
  const [action, setAction] = useState({ approve: false, revision: false, reject: false })
  const [weightIndex, setWeightIndex] = useState({})
  // PGSS-5330: the quantity each line will actually be ordered at, keyed by supplier line id.
  // A supplier quote line carries the quantity the supplier quoted, which is not necessarily what the
  // customer order says now — a quote requested at 15 and revised down to 10 leaves the quote at 15. The
  // customer order is what has to be fulfilled, so it is the default; keeping the quoted figure is a
  // deliberate choice staff make here (extra stock, pack-size rounding) rather than a stale number nobody
  // noticed. Only lines where the two differ appear in here.
  const [orderQuantities, setOrderQuantities] = useState({})
  const [open, setOpen] = useState({});
  const [selectedImage, setSelectedImage] = useState({})
  const [checked, setChecked] = useState(false)
  const expandSelectedSupplier = useRef(true)
  const [selectedSupplierId, setSelectedSupplierId] = useState(null);
  const [vasQuoteDetails, setVasQuoteDetails] = useState([]);
  const { props, param, completeOrderData, name, status, stage, addOnParam, loadOrdersFun } = useSelector(
    ({ eCommerceApp }) => eCommerceApp?.order?.chooseSupplierDialog
  );
  const isVasDataPresent = isValidObject(vasData);
  const [totalVASAmount, setTotalVASAmount] = useState(0);
  const [showSupplierOrderConfirmationDialog, setShowSupplierOrderConfirmationDialog] = useState(false)

  const currentUser = useSelector(({ user }) => user);
  const shouldDisplaySurcharge = displaySurcharge(currentUser);

  // Conditionally disable validation for revision requests
  // Skip chooseSupplierSchema when not creating an order and requesting a revision
  // Allows users to proceed even when Margin = 0 for Product Items
  const shouldSkipValidation = !isCreateOrder && action?.revision;

  const methods = useForm({
    // Set default value
    mode: 'onChange',
    defaultValues: {
      tariff: 0,
      shipping: 0,
      note: '',
      order_details: [],
      vas_price_details: [],
      revision_documents: [],
      new_revision_documents: [],
      surcharge: null,
    },
    resolver: shouldSkipValidation ? undefined : yupResolver(chooseSupplierSchema),
  });

  const {
    control,
    setValue,
    watch,
    reset,
    formState: { errors, isValid, touchedFields },
    getValues,
    setError,
    handleSubmit: formSubmit,
  } = methods;
  const tariff = watch('tariff');
  const shipping = watch('shipping');
  const surcharge = watch('surcharge'); // this is used to make changes in amount and surcharge if any change exists
  const orderDetailsForm = watch('order_details')
  const showRevisionHistoryIcon = completeOrderData?.show_revision_history_icon; // to display revision icon button in the header


  // This function gives the column dynamically based on the display surcharge condition
  const getTariffShippingColumns = () => {
    return shouldDisplaySurcharge
      ? ["Tariff", "Shipping", "Surcharge"]
      : ["Tariff", "Shipping"];
  };

  const highlightedInputStyle = {
    "& .MuiInput-underline:before": {
      borderBottomColor: "#4f46e5", // Default bottom border color
      borderBottomWidth: '2px'
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: "#4f46e5", // Active (focused) bottom border color
    },
    "& .MuiFormLabel-root": {
      color: "#4f46e5", // Label color
    },
  }

  const highlightedBoxInputStyle = {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#4f46e5", // Default border color
        borderWidth: '2px'
      },
      "&:hover fieldset": {
        borderColor: "#4f46e5", // Hover border color
      },
      "&.Mui-focused fieldset": {
        borderColor: "#4f46e5", // Focused border color
      },
    },
    "& .MuiInputLabel-root": {
      color: "#4f46e5", // Default label color
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#4f46e5", // Label color when focused
    },
  }

  useEffect(() => {
    if(props?.open && param) {
      const orderDetail = suppliersList?.[selectedIndex]?.order_details?.map((od) =>
        ({ weight: od.weight, margin: od.product.margin, discount: od.discount, unit_price: od.unit_price, product:{"unit_price": od.product.unit_price}})
      )
      setValue('order_details', orderDetail, {shouldValidate: true});
      if(suppliersList && suppliersList.length > 0 && expandSelectedSupplier.current === true) {
        // Expand the selected supplier row for the first time when user open the dialog
        suppliersList?.forEach((supplierData, index) => {
          if(supplierData.is_selected) {
            setOpen(prev => ({ ...prev, [index]: true }))
            expandSelectedSupplier.current = false
          }
        })
      }
    }
  }, [suppliersList, selectedIndex])

  useEffect(() => {
    if(props?.open && param && !checkNullOrUndefined(selectedIndex) && suppliersList) {
      // Update Unit Price on tariff, shipping value change
      suppliersList?.[selectedIndex]?.order_details?.forEach((od, odIndex) => {
        if(od?.product?.unit_price && Number(od?.product?.unit_price) === 0) return
        const priceVal = calculateUnitPrice(od.weight, od.unit_price, tariff, shipping, od?.product?.margin);
        dispatch(updateQuotedSuppliersList({ field: "unit_price", value: priceVal, selectedIndex, selectedOrder: odIndex }))
      })
    }
  }, [tariff, shipping, selectedIndex])

  const hideForStatus = () => {
    if (status === 'Artwork Inprogress') return true
    return false
  }

  const hideForEdit = () => {
    if (status === 'Artwork Inprogress' || (isCreateOrder && !next) || !isCreateOrder) return true
    return false
  }

  const updateSupplierData = (e, field, selectedOrder, updateVas) => {
    const { value } = e.target
    dispatch(updateQuotedSuppliersList({ field, value, selectedIndex, selectedOrder, updateVas }))
  }

  const supplierHeaders = [
    { name: 'Select',  hide: !isCreateOrder},
    { name: 'Name' },
    { name: `${displayName?.weight}`, hide: hideForStatus() },
    { name: 'Qty', hide: hideForStatus() },
    { name: 'Total FOB', hide: hideForStatus() },
    { name: 'Status', hide: isCreateOrder},
    { name: 'Supplier Notes' },
    { name: 'Actions' },
  ];

  const supplierProductsHeaders = [
    { name: 'No' },
    { name: 'Name' },
    { name: 'Sub Type' },
    { name: 'Weight', hide: hideForStatus() },
    { name: 'Quantity', hide: hideForStatus() },
    { name: 'Price', hide: hideForEdit() },
    { name: 'FOB', hide: hideForStatus() },
    { name: 'Total FOB', hide: hideForStatus() }
  ]

  const supplierProductsHeadersActions = [
    { name: 'No' },
    { name: 'Name' },
    { name: 'Sub Type' },
    { name: `${displayName?.weight}`, hide: hideForStatus() },
    { name: 'Quantity', hide: hideForStatus() },
    { name: 'Sq. ft', hide: hideForStatus() },
    { name: 'Price Per Sq. ft', hide: hideForStatus() },
    { name: 'FOB', hide: hideForStatus() },
    { name: 'Margin (%)', hide: hideForStatus() },
    { name: 'Price ($)', hide: hideForStatus() },
    { name: 'Discount (%)', hide: hideForStatus() },
    { name: 'Amount', hide: hideForStatus() },
  ]

  useEffect(() => {
    if (props?.open && param) {
      setLoading(true);
      let stageName = name === "Choose Supplier for Proof" ? "rfd" : "quote"
      if(completeOrderData?.is_drawing_order) {
        stageName = "order";
      }
      dispatch(
        getQuotedSuppliers({
          id: param,
          params: {
            stage: stageName,
            action: "rfq_to_quote",
          },
        })
      ).then(({payload}) => {
        if(addOnParam && addOnParam?.isCreateSupplierOrder) {
          payload?.orders?.forEach((data) => {
            if(data?.is_selected === true) {
              setSelectedSupplierId(data?.supplier?.id)
            }
          })
        }
      }).finally(() => {
        setLoading(false)
      });
      const vasPriceListParam = completeOrderData?.customer?.id ? {customer_id: completeOrderData?.customer?.id} : {};
      dispatch(
        getVasPriceList(vasPriceListParam)
      )
    }
  }, [props, param]);

  useEffect(() => {
    if (props?.open) {
      reset();
      setSelected('');
      setAccordionIndex(null);
      setNext(true);
      setApprove(false)
      setSelectedImage({})
      setSelectedSupplierId(null)
      // PGSS-5330: quantity choices belong to the order being opened, never to the previous one.
      setOrderQuantities({})
      // Reset TOTAL VAS amount to 0 to prevent incorrect TOTAL display
      // when reopening the Choose Supplier dialog after closing a popup with VAS data
      setTotalVASAmount(0);
      if(addOnParam && addOnParam?.isCreateSupplierOrder) {
        setIsCreateOrder(true)
      } else {
        setIsCreateOrder(false)
      }
      if(addOnParam && addOnParam?.showDrawingConfirmation) {
        setShowOrderDrawingConfirmation(true)
      } else {
        setShowOrderDrawingConfirmation(false)
      }
    }
  }, [reset, props?.open]);

  useEffect(() => {
    suppliersList?.[selectedIndex]?.order_details?.forEach((data, ind) => {
      setWeightIndex(prev => ({ ...prev, [ind]: data.weight }))
    })
  }, [suppliersList, selectedIndex, props.open])

  useEffect(() => {
    const handleWheel = (e) => {
      if (e.target.type === "number") {
        e.target.blur();
      }
    };

    document.addEventListener("wheel", handleWheel, { passive: true });

    return () => {
      document.removeEventListener("wheel", handleWheel);
    };
  }, []);

  const onClose = () => dispatch(closeChooseSupplierDialog());

  // fob - unit_price, wgt - wight, mrg -margin,
  // isPrice - true for custom() - unit_price and Price Column in table
  // isPrice - false Amount Column for calcumating TOTAL amount
  // discount - null -  custom() - unit_price and Price Column in table
  // discount -  Amount Column for calcumating TOTAL amount
  const calculatePrice = (unitPrice, fob, wgt, qty, mrg, isPrice, discount) => {
    const calculate = unitPrice && Number(unitPrice) === 0
    const price = !calculate ? calculateUnitPrice(wgt, fob, tariff, shipping, mrg) : 0
    if (isPrice)
      return price;
    const unitPriceVal = unitPrice && (Number(unitPrice) === 0 || unitPrice > 0) ? Number(unitPrice) : price
    return calculateOrderItemTotalAmount(unitPriceVal, qty, discount);
  }

  const updateUnitPriceAndList = (e, field, fieldName, selectedOrder, orderDetailObj) => {
    if(fieldName === "margin") {
      let { value } = e.target;
      value = value < 0 ? value = '' : value
      // Allow empty values, and ensure the value is a valid number or decimal between 0 and 99.99
      if (value === '' || (/^(\d{1,2}(\.\d{0,2})?)$/.test(value) && Number(value) <= 99.99)) {
        field.onChange(value);
        dispatch(updateQuotedSuppliersList({ field: fieldName, value, selectedIndex, selectedOrder }))
        const price = calculateUnitPrice(orderDetailObj.weight, orderDetailObj.unit_price, tariff, shipping, value)    
        dispatch(updateQuotedSuppliersList({ field: "unit_price", value: price, selectedIndex, selectedOrder }))
      }
    }
    const { value } = e.target
    if(fieldName === "weight") {
      dispatch(updateQuotedSuppliersList({ field: fieldName, value, selectedIndex, selectedOrder }))
      const price = calculateUnitPrice(value, orderDetailObj.unit_price, tariff, shipping, orderDetailObj.product.margin)    
      dispatch(updateQuotedSuppliersList({ field: "unit_price", value: price, selectedIndex, selectedOrder }))
    }
    if(fieldName === "unit_price") {
      // Update Supplier list and update margin
      dispatch(updateQuotedSuppliersList({ field: fieldName, value, selectedIndex, selectedOrder }))
      let marginValue = 0;
      if(value && value > 0) {
        marginValue = calculateMargin(orderDetailObj.weight, orderDetailObj.unit_price, tariff, shipping, value)
      }
      dispatch(updateQuotedSuppliersList({ field: "margin", value: marginValue, selectedIndex, selectedOrder }))
    }
  }

  const handleSubmit = (values) => {
    if (next) setNext(false)
    else {
      if(isVasDataPresent) {
        setVasQuoteDetails(getValues()?.vas_price_details);
        dispatch(updateQuotedSuppliersList({ updateVas: true, value: getValues()?.vas_price_details, selectedIndex }))
      }
      reset();
      setNext(true)
      if(isCreateOrder) {
        setSelected('');
      }
    }
    setApprove(false)
  };

  const custom = () => {
    return parentId?.map((parent, index) => {
      const child = suppliersList[selectedIndex].order_details[index]
      return {
        ...omit(getValues(), ['note']),
        id: parent.product_id,
        margin: child.product?.margin,
        discount: child.discount,
        unit_price: child.product?.unit_price
      }
    })
  }

  const validateApproval = (orderDetails) => {
    let isValidApproval = true
    const formValues = getValues()
    if (!formValues?.tariff && formValues?.tariff !== 0) {
      setError(`tariff`, { message: 'Required' })
      isValidApproval = false
    }
    if (!formValues?.shipping && formValues?.shipping !== 0) {
      setError(`shipping`, { message: 'Required' })
      isValidApproval = false
    }
    orderDetails?.forEach((item, index) => {
      if (!item?.product?.margin && item?.product?.margin !== 0) {
        setError(`order_details.${index}.margin`, { message: 'Required' })
        isValidApproval = false
      }
    })
    return isValidApproval
  }

  const refreshList = () => {
    const tableRefresh = loadOrdersFun || (isDashboard && loadOrders ? loadOrders : null);
    if (tableRefresh) {
      tableRefresh();
    }
    if (stage) {
      dispatch(getOrders({ params: { stage } }));
    }
  };

  const approveOrRejectQuote = (isApprove, note, toStatusCode) => {
    if (isApprove)
      setApprove(true)
    if (isApprove && note === '') {
      setError('note', { message: 'You must provide note' })
      return
    }
    const updateOrderParam = {
        id: suppliersList[selectedIndex].id,
        to_statuses_type_id: toStatusCode,
        updateStatus: 'yes'
      }
    if(action.revision) {
      const formValues = getValues();
      if(formValues?.new_revision_documents && formValues?.new_revision_documents.length > 0) {
        updateOrderParam.new_revision_documents = formValues?.new_revision_documents;
      }
      updateOrderParam.revision_note = note; // Sent Revision Note in revision_note field as BE requested
    } else {
      updateOrderParam.note = note;
    }

    dispatch(
      updateOrder(updateOrderParam)
    ).then((res) => {
      if (!res.error) {
        dispatch(showMessage({ type: 'success', message: 'Success' }));
        refreshList();
      }
    })
    .finally(onClose);
  }

  // On Create Supplier Order button click
  const onCreateSupplierOrderSubmit = (payloadData) => {
    const setSupplierFlag = isCreateOrder === true && next;
    if (showOrderDrawingConfirmation === true) {
      // Show confirmation popup to request for drawing
      setShowSupplierOrderConfirmationDialog(true);
    } else {
      handleCreateSupplierOrder(setSupplierFlag, payloadData);
    }
  };

 // Handle API response
  const handleAPIResponse = (res, successMessage, resetLoading) => {
    if(resetLoading)
      setLoading(false);
    if (!res.error) {
      dispatch(showMessage({
          message: successMessage,
          type: "success",
        })
      );
      refreshList();
    } else {
      dispatch(showMessage({
          message: "Failed",
          type: "error",
        })
      );
    }
  };

  // Handle to Create Supplier Order or Request for drawing
  const handleCreateSupplierOrder = (setSupplier, payloadData, actionType) => {
    if(loading === false)
      setLoading(true);
    const payload = payloadData || {};
    let selectedSuppliersListId = null;
    payload.is_approval_type = checked;
    payload.action_name = "Create Supplier Order";
    payload.change_supplier = !setSupplier;
    if (setSupplier === true) {
      suppliersList?.forEach((data, index) => {
        const { supplier, order_details: od } = data;
        if (supplier.id === selectedSupplierId) {
          selectedSuppliersListId = data.id;
          payload.supplier = { ...supplier };
          payload.supplier.is_selected = true;
          payload.change_supplier = checkNullOrUndefined(data.is_selected);
        }
      });
    } else {
      payload.custom_product = custom();
      selectedSuppliersListId = suppliersList[selectedIndex].id;
    }
    if (actionType === "drawingRequest") {
      // If drawing is required do not create supplier order just update drawing related order status
      const drawingPayload = {
        id: param,
        updateStatus: "yes",
        to_statuses_type_id: addOnParam.toStatusCode,
        action: "request_order_drawing",
        supplier: { id: selectedSuppliersListId },
      }
      if (payload?.is_required_product_images === true)
        drawingPayload.is_required_product_images = true;
      dispatch(updateOrder(drawingPayload))
      .then((res) => {
        handleAPIResponse(res, "The drawing requested successfully", true)
        if(showOrderDrawingConfirmation) {
          resetSupplierOrderConfirmData();
        }
      })
      .finally(onClose);
    } else {
      // PGSS-5330: name the quantity each line is being ordered at, so the supplier order is raised for
      // what staff actually chose. Absent for lines that need no decision — the server then places the
      // order at the customer order's quantity, which is what has to be fulfilled.
      //
      // Sent as `supplier_order_quantities`, NOT `order_details`. On this action `order_details` is a
      // sourcing selection: a non-empty list means "source only these lines", and the server deletes every
      // saved non-stock line whose product is missing from it. These entries carry no product, so putting
      // them there would delete the supplier order's lines as it created them.
      const supplierOrderQuantities = chosenOrderDetails();
      const quoteToOrderPayload = {
          id: param,
          payload: supplierOrderQuantities.length > 0
            ? { ...payload, supplier_order_quantities: supplierOrderQuantities }
            : { ...payload },
          params: { qid: selectedSuppliersListId },
        }

      if (payload?.is_required_product_images === true)
        quoteToOrderPayload.is_required_product_images = true;

      // Create supplier order
      dispatch(quoteToOrder(quoteToOrderPayload))
      .then((res) => {
        handleAPIResponse(res, "Supplier order created successfully", true)
        if(showOrderDrawingConfirmation) {
          resetSupplierOrderConfirmData()
        }
      })
      .finally(onClose);
    }
  };

  const SupplierHandler = ({ note }, isApprove, toStatusCode) => {
    if (isApprove)
      approveOrRejectQuote(isApprove, note, toStatusCode)
    else {
      if (status !== 'Artwork Inprogress' && isVasDataPresent && hasInValidVASPriceDetails(getValues(), vasData)) {
        dispatchMessage(dispatch, 'Enter VAS Price details', 'error');
        return
      }
      setLoading(true);
      let payload = {
        supplier: {},
        new_documents: [],
        note
      };
      let supplierIndex
      suppliersList?.forEach((data, index) => {
        const { supplier, order_details: od } = data;
        if(isCreateOrder && next) {
          if(supplier.is_selected) {
            payload.supplier = { ...supplier };
          }
        } else {
          if (selected === supplier.id) {
            payload.supplier = { ...supplier, is_selected: true, };
            payload.order_details = od;
            supplierIndex = index;
            payload.vas_price_details = getValues().vas_price_details;
          }
          if (accordionIndex !== null && accordionIndex === index) {
            payload.new_documents = data.uploadedFile?.map(({ id }) => ({ id }));
          }
        }
        payload = cloneDeep(payload)
      });
      payload.order_details?.forEach((od, sIndex) => {
        const foundProuduct = suppliersList[accordionIndex]?.order_details?.find(data => data.product.name === od.product.name)
        if (foundProuduct)
          payload.order_details[sIndex].new_drawing_documents = foundProuduct.drawing_documents
        else {
          const image = []
          od?.drawing_documents?.forEach((doc, docIndex) => {
            if (selectedImage?.[supplierIndex]?.[sIndex]?.[docIndex]) {
              image.push(od.drawing_documents[docIndex])
              od.new_drawing_documents = image
            }
          })
          od?.proof_documents?.forEach((doc, docIndex) => {
            if (selectedImage?.[supplierIndex]?.[sIndex]?.[docIndex]) {
              image.push(od.proof_documents[docIndex])
              od.new_proof_documents = image
            }
          })
        }
        delete od.drawing_documents;
        delete od.proof_documents;
        od.weight = weightIndex[sIndex]
      })
      if (status !== 'Artwork Inprogress') {
        if (validateApproval(payload.order_details)) {
          if(isCreateOrder) {
            onCreateSupplierOrderSubmit(payload)
          } else {
            dispatch(saveOrder({ ...payload, isQuote: true, id: param, custom_product: custom() }))
            .then((res) => {
              setLoading(false);
              if (!res.error) {
                dispatch(showMessage({ type: 'success', message: 'Success' }));
                refreshList();
              }
            })
            .finally(onClose);
          }
        } else {
          setLoading(false);
        }
      }
      else {
        const { new_documents: newDocumnets, note: Note, order_details: orderDetails } = payload
        orderDetails?.forEach((od, sIndex) => {
          const image = []
          od?.proof_documents?.forEach((doc, docIndex) => {
            if (selectedImage?.[supplierIndex]?.[sIndex]?.[docIndex]) {
              image.push(od.proof_documents[docIndex])
              od.new_proof_documents = image
            }
          })
          delete od.proof_documents
        })
        const artWorkPayload = {
          new_documents: newDocumnets,
          note: Note,
          order_details: orderDetails,
          id: param
        }
        dispatch(chooseSupplierForArtWork(artWorkPayload)).then((res) => {
          setLoading(false);
          if (!res.error) {
            dispatch(showMessage({ type: 'success', message: 'Success' }));
            refreshList();
          }
        }).finally(onClose)
      }
    }
  }

  function validateDrawingDocuments(supplier, orderDetails, index) {
    const validationErrors = [];
    const arrayIndex = [];
    orderDetails?.forEach((od, sIndex) => {
      const foundProuduct = supplier?.order_details?.find((data) => data.product.name === od.product.name);
      if (foundProuduct)
        orderDetails[sIndex].new_drawing_documents =foundProuduct.drawing_documents;
      else {
        const selectedDrawingDocuments = [];
        const selectedProofDocuments = [];
        od?.drawing_documents?.forEach((doc, docIndex) => {
          if (selectedImage?.[index]?.[sIndex]?.[docIndex]) {
            selectedDrawingDocuments.push(od.drawing_documents[docIndex]);
          }
        });
        od?.proof_documents?.forEach((doc, docIndex) => {
          if (selectedImage?.[index]?.[sIndex]?.[docIndex]) {
            selectedProofDocuments.push(od.proof_documents[docIndex]);
          }
        });
        let drawingDocumentsSelected = true;
        if (od?.drawing_documents && od?.drawing_documents.length > 0 && selectedDrawingDocuments?.length === 0 ) {
          validationErrors.push({row_index: sIndex});
          arrayIndex.push(sIndex + 1);
          drawingDocumentsSelected = false;
        }
        if (drawingDocumentsSelected === true && od?.proof_documents && od?.proof_documents.length > 0 && selectedProofDocuments?.length === 0) {
          validationErrors.push({row_index: sIndex});
          arrayIndex.push(sIndex + 1);
        }
      }
    });
    if (validationErrors && validationErrors.length > 0) {
      return { validationErrors, errorRows: arrayIndex };
    }
    return {};
  }

  const showDrawingConfirmation = (arrayIndex, supplier, od, index) => {
    dispatch(
      openDialog({
        children: (
          <>
            <ConfirmAttachments title="Drawing/Proof Confirmation" message={<div id="alert-dialog-description">
                No drawing/proof selected for order item {_.join(arrayIndex, ", ")}.
                Do you want to proceed?
              </div>}
              onProceed={() => {onSupplierSelect(supplier, od, index)}} />
          </>
        ),
      })
    );
  };

  const handleSupplierSelect = (supplier, od, index, isValidate) => {
    if(isValidate === true) {
      const validationResponse = validateDrawingDocuments(supplier, od, index);
      if(validationResponse && validationResponse.errorRows && validationResponse.errorRows.length > 0) {
        showDrawingConfirmation(validationResponse.errorRows, supplier, od, index);
      } else {
        onSupplierSelect(supplier, od, index,);
      }
    } else {
      onSupplierSelect(supplier, od, index);
    }
  };

  const onSupplierSelect = (supplier, od, index) => {
    setSelected(supplier.id);
    setSelectIndex(index);
    if (shouldDisplaySurcharge === true) {
      // Checks the supplier list for surcharge list and matches with the supplier
      // to populate the surcharge percentage
      const matchingOrder = suppliersList.find(
        (order) => order.supplier && order.supplier.id === supplier.id
      );
      let surchargePercentage = null;
      if (matchingOrder && matchingOrder.surcharges) {
        const matchingSurcharge = matchingOrder.surcharges.find(
          (surCharge) => surCharge.supplier_id === supplier.id
        );
        if (matchingSurcharge && matchingSurcharge.sur_charge_percentage != null) {
          surchargePercentage = Number(matchingSurcharge.sur_charge_percentage);
        }
      }
      setValue("surcharge", surchargePercentage);
    }
    // sets shipping and tariff based on supplier profile or defaults
    if (supplier.profile) {
      setValue('shipping', Number(supplier.profile.shipping) || 0);
      setValue('tariff', Number(supplier.profile.tariff) || 0);
    } else {
      setValue('shipping', 0);
      setValue('tariff', 0);
    }
    handleSubmit();
  }

  const getOrderLabel = () => {
    switch (status) {
      case 'Received Supplier Quote':
        return 'RFQ'
      case 'Artwork Inprogress':
        return 'Order'
      case 'Design Inprogress':
        return 'Order'
      default:
        return 'Order';
    }
  }
  const getCompleteOrderInfo = () => {
    const orderData = _.cloneDeep(completeOrderData);
    orderData.order_details = parentId;
    return orderData;
  };

  const handleVasPreview = () => {
    const orderData = getCompleteOrderInfo();
    openVasPreview(dispatch, vasData, orderData);
  };

  // this method is used for preview of revision history in choose supplier
  const handleRevisionHistoryPreview = () => {
      const copyData = _.cloneDeep(completeOrderData);
      const revisionParam = { order_id: completeOrderData?.id };
      if (copyData?.id) {
        setLoading(true);
        dispatch(getRevisionHistory(revisionParam)).then((response) => {
          setLoading(false);
          if (!response.error) {
            const responseData = response?.payload;
            if (!checkNullOrUndefined(responseData)) {
              const proofData = responseData?.proof ?? [];
              const productionData = responseData?.production ?? [];
              const vasRevision = [...proofData, ...productionData];
              const productData = responseData?.product ?? [];
              dispatch(
                openOrderDialog({
                  data: copyData,
                  param: {
                    name: "revisionHistories",
                    title: "Revision Histories",
                    maxWidth: "lg",
                    hideDialogActions: true,
                    keepOrderData: true,
                    showProductRevision: true,
                    productRevisionData: productData,
                    vasRevisionData: vasRevision ?? [],
                    updateRecords: false,
                    accordianStyle: false,
                  },
                  stage: "order",
                })
              );
            }
          }
        });
      }
    };

  const headerData = {
    enable: true,
    component: OrderHeader,
    data: {
      order: completeOrderData,
      hideAmount: true,
      hideQuantity: status === 'Artwork Inprogress',
      stage: getOrderLabel(),
      showVAS: isVasDataPresent,
      handleVasPreview,
      showRevisionHistory: showRevisionHistoryIcon, // to display the header for Revision History Button
      handleRevisionHistoryPreview // on click of button displays the revision history preview
    },
  };

  const renderImages = (images, index, sInd, showImageSelection = true) => {
    return <>
      <Typography
        variant="h6"
        gutterBottom
        component="div"
        className="mt-6"
      >
        {images ? "Documents" : ""}
      </Typography>
      <div className='flex'>
        {images?.map((media, selectedImageIndex) => {
          return (
            <div>
              <div className='flex'>
                {showImageSelection === true && <div>
                  <Checkbox
                    className='flex-start'
                    checked={selectedImage?.[sInd]?.[index]?.[selectedImageIndex] || false}
                    onChange={(event) => {
                      const newSelectedImages = selectedImage?.[sInd] || [];
                      newSelectedImages[index] = {
                        ...(newSelectedImages[index] || {}),
                        [selectedImageIndex]: event.target.checked
                      };
                      setSelectedImage({ [sInd]: newSelectedImages });
                    }}
                  />
                </div>}
                <div
                  className="justify-center w-128 h-128 rounded-16 mr-12 overflow-hidden outline-none shadow hover:shadow-lg relative cursor-pointer"
                  key={media.id}
                  onClick={() => {onFileViwerClick(media)}}
                  onKeyDown={() => { }}
                  role="button"
                  tabIndex={0}
                >
                  <img
                    src={getFileImageSrc(media)}
                    className="max-w-none w-auto h-full"
                    alt="product"
                    aria-hidden="true"
                  />
                </div>
              </div>
              <Tooltip title={media?.filename || media?.name}>
                <div
                  className='mb-16 mt-10 w-128 text-sm text-center m-auto'
                  style={{
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden'
                  }}>
                    {media?.filename || media?.name}
                </div>
              </Tooltip>
            </div>
          );
        })}
      </div>
    </>
  }

  const getTitle = () => {
    let title = 'Approve'
    if (action.revision) title = 'Request Revision'
    return title
  }

  const getWidth = () => {
    if (status === 'Artwork Inprogress') return '850px'
    if (completeOrderData?.image_customisation === 'vas') return '550px'
    return 'auto'
  }

  const onChange = (rowIndex, value) => {
    setWeightIndex(prev => ({ ...prev, [rowIndex]: value }))
  }

  /**
   * PGSS-5330: true when this supplier line was quoted at a different quantity from the one the customer
   * order now carries, so staff have a decision to make. `customer_quantity` is null on lines with no
   * customer line behind them — an inventory / stock-replenishment order — and those are left alone.
   *
   * <p><b>Scoped to Create Supplier Order only.</b> This dialog serves four flows — the two approval
   * routes, the VAS/Stone views and this one — and only here is a supplier order actually raised, so only
   * here is there a quantity to decide. `isCreateOrder` is the flag the file already uses for exactly this
   * (see the PGSS-4961 in-stock note), set from `addOnParam.isCreateSupplierOrder`. Without the gate the
   * other three flows would grow a dropdown that decides nothing.
   */
  const hasQuantityChoice = (line) =>
    isCreateOrder === true &&
    line?.customer_quantity !== null &&
    line?.customer_quantity !== undefined &&
    Number(line.customer_quantity) !== Number(line.quantity);

  /** The quantity this line will be ordered at: whatever staff picked, defaulting to the customer's. */
  const orderedQuantityFor = (line) => {
    if (!hasQuantityChoice(line)) return line?.quantity;
    const chosen = orderQuantities[line.id];
    return chosen === undefined ? line.customer_quantity : chosen;
  };

  const setOrderQuantity = (lineId, quantity) =>
    setOrderQuantities((current) => ({ ...current, [lineId]: quantity }));

  /**
   * The per-line quantities to send with Create Supplier Order. Only lines where the quoted and customer
   * quantities differ are sent; everything else keeps the server's own resolution.
   */
  const chosenOrderDetails = () => {
    // hasQuantityChoice is already gated on isCreateOrder, so the other flows through this dialog return
    // an empty list here and their payloads are untouched.
    //
    // Gathered across every supplier rather than just the selected index: Create Supplier Order is reached
    // by two routes, one keyed on the selected index and one that resolves the supplier by id, and both
    // render these rows. Line ids are unique, and the server matches overrides against the chosen quote's
    // lines only, so any extras are ignored rather than misapplied.
    return (suppliersList || [])
      .flatMap((supplier) => supplier?.order_details || [])
      .filter((line) => hasQuantityChoice(line))
      // Keyed on the CUSTOMER line, not this line's own id. The server pairs quote and supplier lines by
      // position, so a choice keyed on the quote line could be applied to a different product; the
      // customer line is the stable identity both sides already share.
      .map((line) => ({
        parent_order_detail_id: line.parent_order_detail_id,
        quantity: orderedQuantityFor(line),
      }));
  };

  const getProductDetail = (products, index, isWeightEditable, allFiles, sInd, isSelectedSupplier = false) => {
    // suppliersList -> sList?.order_details?.map((pdetail, ind) => {
    // products - pdetail(order_detail)
    const showImageSelect = !isCreateOrder || (isCreateOrder && !isSelectedSupplier)
    // PGSS-4961: on the Create Supplier Order screen, in-stock products are NOT sourced here — they ship
    // from inventory, and any shortfall is replenished via the separate "Create Supplier Inventory Order"
    // flow. Show such lines grayed/disabled with a note instead of hiding them (the backend also enforces
    // this). Non-stock and custom products (is_stock_product !== true) stay active. Scoped to isCreateOrder
    // so Stone/VAS/approval views are unaffected.
    // PGSS-4978: only gray for a MIXED cart (both stock and non-stock present). An all-stock supplier order
    // (e.g. RFQ/CAD sourced) must stay fully interactive — graying every row blocks the whole order.
    const csoLines = suppliersList?.[sInd ?? selectedIndex]?.order_details
      || suppliersList?.[selectedIndex]?.order_details || [];
    const isInStockLine = isCreateOrder === true && isMixedSupplierCart(csoLines)
      && products?.product?.is_stock_product === true;
    const lineShortfall = Number(products?.quantity ?? 0) - Number(products?.confirmed_quantity ?? 0);
    const inStockNote = lineShortfall > 0
      ? `Short by ${lineShortfall} — replenish via Create Supplier Inventory Order`
      : 'In stock — ships from inventory, no sourcing needed';
    return (
      <>
        <TableRow sx={isInStockLine ? { opacity: 0.55, backgroundColor: '#f5f5f5', pointerEvents: 'none' } : undefined}>
          <TableCell className="px-8 py-4" style={{width:"50px"}}>
            {index+1}
          </TableCell>
          <TableCell className="px-8 py-4" style={{minWidth:"400px", maxWidth:"400px"}}>
            {products.name}
            {isInStockLine && (
              <Tooltip title={inStockNote}>
                <span style={{ marginLeft: 6, verticalAlign: 'middle', cursor: 'help', display: 'inline-flex', pointerEvents: 'auto' }}>
                  <FuseSvgIcon color={lineShortfall > 0 ? 'warning' : 'action'} size={18}>
                    {lineShortfall > 0 ? 'material-outline:production_quantity_limits' : 'material-outline:inventory_2'}
                  </FuseSvgIcon>
                </span>
              </Tooltip>
            )}
          </TableCell>
          <TableCell className="px-8 py-4" style={{width:"120px"}}>
            {products.sub_type}
          </TableCell>
          {status !== 'Artwork Inprogress' && <>
            <TableCell className="px-8 py-4">
              {
                isWeightEditable ? (
                  <Controller
                    control={control}
                    name={`order_details.${index}.weight`}
                    rules={{
                      required: {
                        value: true,
                        message: 'Required'
                      }
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="number-no-scroll w-60"
                        type="number"
                        onKeyDown={preventNegativeValues}
                        onChange={(e) => {
                          field.onChange(e.target.value)
                          updateUnitPriceAndList(e, field, 'weight', index, products)
                        }}
                        variant="standard"
                        size="small"
                        label=''
                        required
                        error={!!errors?.order_details?.[index]?.weight}
                        helperText={errors?.order_details?.[index]?.weight?.message}
                        sx={highlightedInputStyle}
                        disabled={action?.reject || action?.revision}
                      />
                    )}
                  />
                ) : <>{products.weight} lbs</>
              }
            </TableCell>
            {/* Quantity */}
            <TableCell className="px-8 py-4">
              {hasQuantityChoice(products) ? (
                // PGSS-5330: the supplier quoted one number and the customer order now says another.
                // Show both and make staff pick, rather than silently ordering the quoted figure — that
                // is how a supplier order was raised for 15 against a customer order of 10, shipped 15,
                // and then failed to receive.
                //
                // The control belongs to the editable table only — the supplier list above it is for
                // comparing candidates, so it states the resolved quantity without offering the choice
                // twice. Both carry the same explanatory line so the two never appear to disagree.
                <div className="flex flex-col gap-4">
                  {isWeightEditable ? (
                    <TextField
                      select
                      size="small"
                      variant="outlined"
                      value={String(orderedQuantityFor(products))}
                      onChange={(e) => setOrderQuantity(products.id, Number(e.target.value))}
                      SelectProps={{ native: true }}
                      inputProps={{ 'aria-label': 'Quantity to order from the supplier' }}
                    >
                      <option value={products.customer_quantity}>
                        {products.customer_quantity} (customer order)
                      </option>
                      <option value={products.quantity}>
                        {products.quantity} (as quoted)
                      </option>
                    </TextField>
                  ) : (
                    orderedQuantityFor(products)
                  )}
                  <Typography className="text-11 text-grey-600">
                    Quoted {products.quantity}, customer order {products.customer_quantity}
                  </Typography>
                </div>
              ) : (
                products.quantity
              )}
            </TableCell>
            {/* Square feet */}
            {isWeightEditable && <TableCell className="px-8 py-4">
              {products.product?.square_feet}
            </TableCell>}
            {/* Price Per Square feet */}
            {isWeightEditable && <TableCell className="px-8 py-4">
              {products.product?.price_per_square_feet}
            </TableCell>}
            {/* Price */}
            {isCreateOrder && next && <TableCell className="px-8 py-4">
              {isSelectedSupplier === true ? formatter.format(products.product.unit_price) : "-"}
            </TableCell>}
            {/* FOB */}
            <TableCell className="px-8 py-4">
              {formatter.format(products.unit_price)}
            </TableCell>
            <>
              {
                isWeightEditable && (
                  <TableCell className="px-8 py-4">
                    <Controller
                      control={control}
                      name={`order_details.${index}.margin`}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          className="number-no-scroll w-60"
                          type="number"
                          onKeyDown={preventNegativeValues}
                          onChange={(e) => {
                            updateUnitPriceAndList(e, field, 'margin', index, products)
                          }}
                          InputLabelProps={{ shrink: true }}
                          error={!!errors?.order_details?.[index]?.margin}
                          helperText={errors?.order_details?.[index]?.margin?.message}
                          variant="standard"
                          size="small"
                          label=''
                          sx={highlightedInputStyle}
                          disabled={action?.reject || action?.revision}
                        />
                      )}
                    />
                  </TableCell>
                )}
                {/* Price column */}
                {isWeightEditable && (
                  <TableCell className="px-8 py-4" style={{width:"100px"}}>
                    <Controller
                      control={control}
                      name={`order_details.${index}.product.unit_price`}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          className="number-no-scroll w-100"
                          type="number"
                          onKeyDown={preventNegativeValues}
                          onChange={(e) => {
                            field.onChange(e.target.value)
                            updateUnitPriceAndList(e, field, 'unit_price', index, products)
                          }}
                          InputLabelProps={{ shrink: true }}
                          error={!!errors?.order_details?.[index]?.product?.unit_price}
                          helperText={errors?.order_details?.[index]?.product?.unit_price?.message}
                          variant="standard"
                          size="small"
                          label=''
                          sx={highlightedInputStyle}
                          disabled={action?.reject || action?.revision}
                        />
                      )}
                    />
                  </TableCell>
                )}
              {/* Discount column */}
              {isWeightEditable && <TableCell className="px-8 py-4">
                <Controller
                      control={control}
                      name={`order_details.${index}.discount`}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          className="number-no-scroll w-60"
                          type="number"
                          onKeyDown={preventNegativeValues}
                          onChange={(e) => {
                            const { value } = e.target;
                            if (value === '' ||  (Number(value) >= 0 && Number(value) <= 99.99)) {
                              field.onChange(e.target.value)
                              updateSupplierData(e, 'discount', index)
                            }
                          }}
                          InputLabelProps={{ shrink: true }}
                          error={!!errors?.order_details?.[index]?.discount}
                          helperText={errors?.order_details?.[index]?.discount?.message}
                          variant="standard"
                          size="small"
                          label=''
                          sx={highlightedInputStyle}
                          disabled={action?.reject || action?.revision}
                        />
                      )}
                    />
              </TableCell>}
              {/* Amount column or Total FOB */}
              {isWeightEditable ?
              <TableCell className="px-8 py-4">
                {formatter.format(calculatePrice(products.product?.unit_price, products.unit_price, products.weight, products.quantity, products.product?.margin, false, products.discount))}
              </TableCell> :
              <TableCell className="px-8 py-4">
              {formatter.format(products.unit_price * products.quantity)}
            </TableCell>}
            </>
          </>}
        </TableRow>
        {!isWeightEditable && (products.drawing_documents || products.proof_documents) && (
          <TableRow>
            <TableCell colspan="100%" >
            <Box sx={{ margin: 2 }}>
              <Collapse in timeout="auto" unmountOnExit>
                {renderImages(products.drawing_documents || products.proof_documents, index, sInd, showImageSelect)}
              </Collapse>
            </Box></TableCell>
          </TableRow>
        )}
      </>
    )
  }

  useEffect(() => {
    // Reset all expanded rows
    setOpen({})
    if(!props?.open) {
      expandSelectedSupplier.current = true
      setSelectIndex(null);
      setChecked(false);
    }
    suppliersList?.forEach((data) => {
      data?.order_details?.forEach((od, key) => {
        setOpen(prev => ({ ...prev, [key]: false }))
      })
    })
  }, [props?.open])

  const additionalBtns = () => {
    if(isCreateOrder === true && next) {
      // PGSS-4978: do NOT block submit when there's no non-stock line. An all-stock supplier order (e.g.
      // RFQ/CAD sourced) is valid; the mixed-cart non-stock sourcing is enforced by the backend filter, not
      // by disabling the button. (Previously `!hasNonStockLine` left the Create Supplier Order button
      // permanently disabled for all-stock orders.)
      return <>
        <Button
            variant="contained"
            color="secondary"
            type="submit"
            onClick={formSubmit((data) => {onCreateSupplierOrderSubmit(null)})}
            disabled={loading || !isEmpty(errors)}
          >
            Create Supplier Order
        </Button>
      </>
    }
    if(!next) {
      return <>
        <Button
          variant="contained"
          color="secondary"
          type="submit"
          onClick={handleSubmit}
          disabled={loading || !selected}
        >
          Previous
        </Button>
        {!isCreateOrder && action.approve && (
          <Button
            variant="contained"
            color="success"
            type="submit"
            onClick={formSubmit((data) => SupplierHandler(data, false))}
            disabled={loading || !isEmpty(errors) || (!_.isEmpty(touchedFields) && !isValid)}
          >
            Submit For Approval
          </Button>
        )}
        {!isCreateOrder && action.revision && (
          <Button
            variant="contained"
            color="warning"
            type="submit"
            onClick={formSubmit((data) => SupplierHandler(data, true, 'QUOTE_REVISIONS_REQUESTED'))}
          >
            Request Revision
          </Button>
        )}
        {/* Currently we are not having to go to next edit page for create supplier order */}
        {/* {isCreateOrder && (
          <Button
              variant="contained"
              color="secondary"
              type="submit"
              onClick={formSubmit((data) => SupplierHandler(data, false))}
              disabled={loading || !isEmpty(errors)}
            >
              Create Supplier Order
          </Button>
        )} */}
      </>
    }
    return <></>
  }

  const getTableHeaderColor = (supplier, index) => {
    if(isCreateOrder && supplier.is_selected) {
      return "#99ffb4"
    }
    if(isCreateOrder && !supplier.is_selected) {
      return open[index] ? "#0eabff80" : "white"
    }
    return  open[index] ? "#0eabff80" : "white"
  }

  const onChoosenSuplierChange = (value) => {
    setSelectedSupplierId(value);
  };

  function handleSupplierOrderDialogSubmit() {
    setShowSupplierOrderConfirmationDialog(false);
    const setSupplierFlag = isCreateOrder === true && next;
    const dataCopy = _.cloneDeep(getValues());
    if (dataCopy?.is_drawing_required === true) {
      // If drawing is required do not create supplier order just update drawing related order status
      handleCreateSupplierOrder(
        setSupplierFlag,
        dataCopy,
        "drawingRequest"
      );
    } else {
      // If drawing is not need, follow existing flow, which is create supplier order
      // we have to call the different API If Drawing is not required
      handleCreateSupplierOrder(setSupplierFlag, dataCopy);
    }
  }

  function closeSupplierOrderConfirmDialog(data) {
    setShowSupplierOrderConfirmationDialog(false)
    setValue('is_drawing_required', undefined)
    setValue('is_required_product_images', undefined)
  }

  function resetSupplierOrderConfirmData() {
    setValue('is_drawing_required', undefined)
    setValue('is_required_product_images', undefined)
  }

  // PGSS-4928: the surcharge the preview should use must match what the backend will save.
  // The save path (SurchargeResolver) treats a BLANK surcharge as "apply the default"
  // (customer profile → Settings) and an explicit 0 as "no surcharge". Mirror that here so the
  // previewed amount equals the saved amount — otherwise the quote total jumps after save when the
  // user clears the field.
  const getEffectiveSurchargePercentage = () => {
    const raw = getValues("surcharge");
    // Explicit value (including 0) wins — 0 means "no surcharge".
    if (raw !== null && raw !== undefined && raw !== "") {
      return Number(raw) || 0;
    }
    // Blank → resolved default for the selected supplier (same value used to pre-fill the field).
    const selectedSupplier = suppliersList?.[selectedIndex];
    const matchingSurcharge = selectedSupplier?.surcharges?.find(
      (s) => s.supplier_id === selectedSupplier?.supplier?.id
    );
    return matchingSurcharge && matchingSurcharge.sur_charge_percentage != null
      ? Number(matchingSurcharge.sur_charge_percentage)
      : 0;
  };

  const selectedSupplierOrderDetails = suppliersList?.[selectedIndex]?.order_details || [];
  const isMixedCsoSummary = isCreateOrder === true && isMixedSupplierCart(selectedSupplierOrderDetails);
  const csoSummaryLines = isMixedCsoSummary
    ? selectedSupplierOrderDetails.filter((od) => od?.product?.is_stock_product !== true)
    : selectedSupplierOrderDetails;

  // PGSS-5006: Create Supplier Order summary totals use supplier FOB (unit_price × qty).
  const calculateSummaryLineTotal = (orderDetail, index) => {
    if (isCreateOrder) {
      // PGSS-5025: exact decimal, not Number()×Number() — but keep the Number return type: the summary
      // total at ~1853 accumulates with `sum + …`, which would CONCATENATE if this returned a string.
      return Number(calculateLineTotalExact(orderDetail?.unit_price, orderDetail?.quantity, 0));
    }
    const lineWeight = isMixedCsoSummary ? Number(orderDetail?.weight) : weightIndex[index];
    return Number(
      calculatePrice(
        orderDetail?.product?.unit_price,
        orderDetail?.unit_price,
        lineWeight,
        orderDetail?.quantity,
        orderDetail?.product?.margin,
        false,
        orderDetail?.discount
      )
    );
  };

  const calculateSurchargeAmount = () => {
    if (selectedIndex === undefined || !suppliersList) return 0;

    // PGSS-4928: blank → resolved default; explicit 0 → none (so preview == saved).
    const surchargePercentage = getEffectiveSurchargePercentage();
    if (!surchargePercentage) return 0; // explicit 0 / no default → not calculated

    // PGSS-4979: mixed CSO surcharge subtotal matches sourced non-stock lines only.
    const linesForSubtotal = isMixedCsoSummary ? csoSummaryLines : selectedSupplierOrderDetails;

    // Calculate subtotal (products + VAS) — PGSS-5025: sum in exact decimal, not floats.
    const subtotal = addMoneyExact(
      ...(linesForSubtotal || []).map((o, index) => calculateSummaryLineTotal(o, index))
    );

    // Calculate surcharge amount - this excludes vas amount in the calc.
    // PGSS-5025: this previously did NOT round at all — `(subtotal * pct) / 100` in floats — so the
    // previewed surcharge could differ from the saved one. Now exact decimal HALF_UP to 2dp, identical to
    // the backend's OrderCalculationService.calculateSurchargeAmount.
    return Number(calculateSurchargeExact(subtotal, surchargePercentage));
  };

  return (
    <FormProvider {...methods}>
      <AuraBaseDialog
        dialogProps={{ open: props?.open }}
        closeDialog={onClose}
        disableCloseIcon={loading}
        dialogParam={{
          maxWidth: "xl",
          title: next ? name : getTitle(),
          hideDialogActions: true,
          style: {
            contentSxStyle: {
              padding: "16px !important",
            },
          },
        }}
        disableSubmitButton={() => false}
        header={headerData}
        additionalButton={additionalBtns()}
      >
        {loading ? (
          <Loader />
        ) : (
          <>
            {next && (
              <div className="table-responsive w-full">
                {isCreateOrder && (
                  <div className="mx-10">
                    <Checkbox
                      checked={checked}
                      className="px-8 py-8"
                      onChange={() => {
                        setChecked(!checked);
                      }}
                    />
                    Send for Approval
                  </div>
                )}
                <Table className="simple w-full min-w-full">
                  <AuraSimpleTableHead
                    headerList={supplierHeaders}
                    classes={{ cnCell: "font-semibold px-8 py-8" }}
                  />
                  <TableBody>
                    {suppliersList?.map((data, index) => {
                      const {
                        supplier = {},
                        order_details: od = [],
                        uploadedFile,
                      } = data;
                      const hasDrawingDocuments = od?.some(
                        (d) => d.drawing_documents || d.proof_documents
                      );
                      const allFiles = [];
                      if (uploadedFile?.length) allFiles.push(...uploadedFile);
                      od?.forEach((l) => {
                        if (l.drawing_documents)
                          allFiles.push(...l.drawing_documents);
                        if (l.proof_documents)
                          allFiles.push(...l.proof_documents);
                      });

                      return (
                        <Fragment key={index}>
                          <TableRow
                            style={{
                              backgroundColor: getTableHeaderColor(
                                supplier,
                                index
                              ),
                              borderColor:
                                isCreateOrder &&
                                selectedSupplierId === supplier?.id
                                  ? "#99FFB4"
                                  : "",
                            }}
                            className={`${
                              isCreateOrder &&
                              selectedSupplierId === supplier?.id &&
                              "border-2"
                            }`}
                          >
                            {isCreateOrder && (
                              <TableCell scope="row" style={{ width: "50px" }}>
                                <Radio
                                  checked={selectedSupplierId === supplier?.id}
                                  onChange={() =>
                                    onChoosenSuplierChange(supplier?.id)
                                  }
                                  value={index}
                                  name="radio-buttons"
                                  inputProps={{ "aria-label": { index } }}
                                  color="success"
                                />
                              </TableCell>
                            )}
                            <TableCell scope="row">
                              <div className="flex flex-row">
                                {allFiles?.length > 0 && (
                                  <Tooltip title="Attachment included">
                                    <FuseSvgIcon
                                      color="success"
                                      size={20}
                                      className="text-20 mr-10"
                                    >
                                      material-outline:file_present
                                    </FuseSvgIcon>
                                  </Tooltip>
                                )}
                                <Typography
                                  className={
                                    allFiles?.length > 0 && "font-semibold"
                                  }
                                >
                                  {supplier.name}
                                </Typography>
                              </div>
                            </TableCell>
                            {status !== "Artwork Inprogress" && (
                              <>
                                <TableCell scope="row">
                                  <Typography>
                                    {formatWeight(data.weight)}
                                  </Typography>
                                </TableCell>
                                <TableCell scope="row">
                                  <Typography>{data.quantity}</Typography>
                                </TableCell>
                                <TableCell scope="row">
                                  <Typography>
                                    {formatter.format(data.amount)}
                                  </Typography>
                                </TableCell>
                              </>
                            )}
                            {/* {suppliersList?.length > 1 &&
                            <TableCell scope="row">
                              {allFiles.length ? (
                                <Switch
                                  size="small"
                                  checked={index === accordionIndex}
                                  onClick={(e) => setAccordionIndex(e.target.checked ? index : null)}
                                />
                              ) : null}
                            </TableCell>
                          } */}
                            {!isCreateOrder && (
                              <TableCell scope="row">
                                <Typography>{data.status?.name}</Typography>
                              </TableCell>
                            )}
                            <TableCell scope="row">
                              <Typography>{data.supplier_notes}</Typography>
                            </TableCell>
                            <TableCell scope="row">
                              {["Requested", "Submitted"].includes(
                                data.status?.name
                              ) && (
                                <div className="flex">
                                  {!isCreateOrder &&
                                    name !== "Revisions To Supplier" && (
                                      <Tooltip title="Approve">
                                        <FuseSvgIcon
                                          onClick={() => {
                                            setAction({
                                              approve: true,
                                              revision: false,
                                              reject: false,
                                            });
                                            handleSupplierSelect(
                                              supplier,
                                              od,
                                              index,
                                              true
                                            );
                                          }}
                                          className="text-48 cursor-pointer"
                                          size={24}
                                          color="action"
                                        >
                                          material-outline:done
                                        </FuseSvgIcon>
                                      </Tooltip>
                                    )}
                                  {!isCreateOrder && (
                                    <Tooltip title="Request Revision">
                                      <FuseSvgIcon
                                        onClick={() => {
                                          setAction({
                                            approve: false,
                                            revision: true,
                                            reject: false,
                                          });
                                          handleSupplierSelect(
                                            supplier,
                                            od,
                                            index,
                                            true
                                          );
                                        }}
                                        className="text-48 cursor-pointer"
                                      >
                                        material-outline:restart_alt
                                      </FuseSvgIcon>
                                    </Tooltip>
                                  )}
                                  <IconButton
                                    aria-label="expand row"
                                    size="small"
                                    onClick={() =>
                                      setOpen((prev) => ({
                                        ...prev,
                                        [index]: !prev[index],
                                      }))
                                    }
                                    title="Show Product Detail"
                                  >
                                    <Icon>
                                      {open[index]
                                        ? "keyboard_arrow_up"
                                        : "keyboard_arrow_down"}
                                    </Icon>
                                  </IconButton>
                                </div>
                              )}
                            </TableCell>
                          </TableRow>

                          <TableRow>
                            <TableCell
                              style={{ paddingBottom: 0, paddingTop: 0 }}
                              colSpan={10}
                            >
                              {open[index] && (
                                <Table>
                                  <AuraSimpleTableHead
                                    headerList={supplierProductsHeaders}
                                    classes={{
                                      cnCell: "font-semibold px-8 py-8",
                                    }}
                                  />
                                  <TableBody>
                                    {suppliersList?.map((sList, sInd) => {
                                      if (sInd !== index) return null;
                                      return sList?.order_details?.map(
                                        (pdetail, ind) => {
                                          return (
                                            <>
                                              {getProductDetail(
                                                pdetail,
                                                ind,
                                                false,
                                                allFiles,
                                                sInd,
                                                sList.is_selected
                                              )}
                                              <TableRow>
                                                <TableCell
                                                  colspan="100%"
                                                  style={{
                                                    padding: 0,
                                                    border: 0,
                                                  }}
                                                >
                                                  <Divider
                                                    sx={{ width: "100%" }}
                                                  />
                                                </TableCell>
                                              </TableRow>
                                            </>
                                          );
                                        }
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              )}
                              {/* < Box sx={{ margin: 5 }}>
                              <Collapse in timeout="auto" unmountOnExit>
                                {renderImages(allFiles)}
                              </Collapse>
                            </Box> */}
                            </TableCell>
                          </TableRow>
                        </Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
            {!next && (
              <>
                <div className="table-responsive w-full">
                  {/* PGSS-4978: show the non-stock sourcing note only for a MIXED cart (stock + non-stock).
                      An all-stock supplier order is valid and must not show this note. */}
                  {isCreateOrder && isMixedSupplierCart(selectedSupplierOrderDetails) && (
                    <Typography variant="caption" className="block mx-10 mb-4" sx={{ color: 'text.secondary' }}>
                      Only non-stock items are sourced on a supplier order. In-stock items ship from inventory;
                      shortfalls are replenished via <b>Create Supplier Inventory Order</b>.
                    </Typography>
                  )}
                  {isCreateOrder && (
                    <div className="mx-10">
                      <Checkbox
                        checked={checked}
                        className="px-8 py-8"
                        onChange={() => {
                          setChecked(!checked);
                        }}
                      />
                      Send for Approval
                    </div>
                  )}
                  <Table
                    className="simple w-full min-w-full"
                    sx={{
                      "&.simple tbody tr td:first-of-type": {
                        padding: "8px",
                      },
                      "&.simple thead tr th:first-of-type": {
                        padding: "8px",
                      },
                      "&.simple thead tr th": {
                        padding: "8px",
                        backgroundColor: "#f0f3fb",
                        fontWeight: 600,
                      },
                    }}
                  >
                    <AuraSimpleTableHead
                      headerList={supplierProductsHeadersActions}
                      classes={{ cnCell: "font-semibold px-8 py-8" }}
                    />
                    <TableBody>
                      {selectedIndex !== undefined &&
                        suppliersList &&
                        suppliersList[selectedIndex]?.order_details?.map(
                          (products, index) => {
                            return (
                              <Fragment key={products.id}>
                                {getProductDetail(products, index, true)}
                              </Fragment>
                            );
                          }
                        )}
                    </TableBody>
                  </Table>
                </div>
                {isVasDataPresent && (
                  <VasPriceList
                    type="add"
                    from="chooseSupplier"
                    vasData={vasData}
                    completeOrderData={getCompleteOrderInfo()}
                    totalVASAmount={totalVASAmount}
                    setTotalVASAmount={setTotalVASAmount}
                  />
                )}
                <div className="flex gap-10 justify-between mt-32">
                  <div>
                    {status !== "Artwork Inprogress" && (
                      <div className="flex gap-6">
                        {/* Dynamically populates the label based on display surcharge condition */}
                        {getTariffShippingColumns().map((label) => (
                          <Controller
                            control={control}
                            key={label}
                            name={label.toLowerCase()}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                className="mb-24"
                                label={
                                  getTariffShippingColumns().includes(label)
                                    ? `${label} (%)`
                                    : label
                                }
                                id={label.toLowerCase()}
                                InputLabelProps={{ shrink: true }}
                                variant="outlined"
                                fullWidth
                                size="small"
                                type="number"
                                onKeyDown={preventNegativeValues}
                                error={!!errors[label.toLowerCase()]}
                                helperText={
                                  errors[label.toLowerCase()]?.message ||
                                  // PGSS-4882: tell users blank applies the default surcharge, 0 = none.
                                  (label === "Surcharge"
                                    ? "Leave blank to apply the default surcharge; enter 0 for no surcharge."
                                    : undefined)
                                }
                                InputProps={{
                                  startAdornment: ["Shipping"].includes(
                                    label
                                  ) && (
                                    <InputAdornment position="start">
                                      $
                                    </InputAdornment>
                                  ),
                                  inputProps: { min: 0 },
                                }}
                                disabled={action?.reject || action?.revision}
                                sx={highlightedBoxInputStyle}
                              />
                            )}
                          />
                        ))}
                      </div>
                    )}
                    {/* PGSS-5043: read supplier_notes, not note. `note` is the supplier order's latest
                        note whoever wrote it last — and staff's own approval / revision note is posted
                        against this same supplier order, so it overwrote it and was then shown back to
                        staff labelled "Supplier Notes". supplier_notes is the supplier's own text from
                        their quote submission, which is what this read-only field is for; it is already
                        on these objects (the table column above renders it). */}
                    {selectedIndex !== undefined &&
                      suppliersList &&
                      suppliersList[selectedIndex]?.supplier_notes && (
                        <div className="w-100 mb-24 w-full">
                          <TextField
                            className="w-100 w-full"
                            id="supplier_note"
                            label="Supplier Notes"
                            value={suppliersList[selectedIndex]?.supplier_notes}
                            disabled
                            multiline
                            rows={5}
                            variant="outlined"
                            sx={highlightedBoxInputStyle}
                          />
                        </div>
                      )}
                    <div style={{ width: getWidth() }}>
                      <Controller
                        control={control}
                        name="note"
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Notes"
                            required={approve}
                            multiline
                            minRows={3}
                            maxRows={5}
                            fullWidth
                            error={!!errors.note}
                            helperText={errors.note?.message}
                            sx={highlightedBoxInputStyle}
                          />
                        )}
                      />
                    </div>
                  </div>

                  {status !== "Artwork Inprogress" && (
                    <div className="flex justify-end flex-col w-1/3">
                      <Table className="simple">
                        <TableBody>
                          <TableRow>
                            <TableCell>
                              <Typography className="font-normal text-13 ">
                                Total Item(s)
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography className="font-normal">
                                {selectedIndex !== undefined &&
                                  suppliersList &&
                                  (isMixedCsoSummary
                                    ? sumBy(csoSummaryLines, (o) => Number(o?.quantity ?? 0))
                                    : sumBy(
                                        [suppliersList[selectedIndex]],
                                        function (o) {
                                          return o?.quantity;
                                        }
                                      ))}
                              </Typography>
                            </TableCell>
                          </TableRow>

                          <TableRow>
                            <TableCell>
                              <Typography className="font-normal text-13 ">
                                Total Weight
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography className="font-normal">
                                {selectedIndex !== undefined &&
                                  suppliersList &&
                                  _.sumBy(
                                    _.map(
                                      isMixedCsoSummary ? csoSummaryLines : selectedSupplierOrderDetails,
                                      (o, index) => {
                                        const quantity = Number(o?.quantity);
                                        const itemWeight = isMixedCsoSummary
                                          ? Number(o?.weight)
                                          : weightIndex[index];
                                        return quantity * itemWeight;
                                      }
                                    ),
                                    _.identity
                                  )}{" "}
                                lbs
                              </Typography>
                            </TableCell>
                          </TableRow>
                          
                          {/* To display the surcharge amount if enabled  */}
                          {shouldDisplaySurcharge && (
                            <TableRow>
                              <TableCell>
                                <Typography className="font-normal text-13 ">
                                  Additional Surcharge Amount
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography className="font-normal">
                                  {formatter.format(calculateSurchargeAmount())}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                          <TableRow>
                            <TableCell>
                              <Typography className="font-bold text-13">
                                TOTAL
                              </Typography>
                            </TableCell>
                            {/* Calculates total amount with vas+surcharge */}
                            <TableCell align="right">
                              <div className="flex flex-row justify-end">
                                <Typography
                                  className="font-normal"
                                  variant="h6"
                                >
                                  {selectedIndex !== undefined &&
                                    suppliersList &&
                                    formatter.format(
                                      (isMixedCsoSummary ? csoSummaryLines : selectedSupplierOrderDetails).reduce(
                                        (sum, o, index) => sum + calculateSummaryLineTotal(o, index),
                                        0
                                      ) +
                                        (totalVASAmount || 0) +
                                        calculateSurchargeAmount()
                                    )}
                                </Typography>
                                <Typography
                                  className="font-normal text-red"
                                  variant="h6"
                                >
                                  *
                                </Typography>
                              </div>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
                {action.revision && (
                  <div className="border-gray-400 bg-white">
                    <div className="flex-row">
                      <Attachments
                        category="document"
                        addTo="revision"
                        allowAdd
                        attachmentKey="revision_documents"
                        attachmentsType="revision_documents"
                        allowDelete
                        style={{ rows: true }}
                        width="w-[12rem]"
                        height="h-[9rem]"
                        fontSize="text-12"
                        smallButton
                        payload={{ image_customisation_type: "revision" }}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </AuraBaseDialog>
      <SupplierOrderConfirmationDialog
        open={showSupplierOrderConfirmationDialog}
        onClose={closeSupplierOrderConfirmDialog}
        onSubmit={handleSupplierOrderDialogSubmit}
      />
    </FormProvider>
  );
}

export default memo(ChooseSupplier);